import dayjs from 'dayjs'
import cx from 'classnames'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { entries, groupBy, last } from 'lodash'

import { ChatRoom, Message } from 'lib/_types'
import { useCurrentUser } from 'store/api/yay/user'
import { useInfinityScrollSimple, useScroll } from 'hooks'
import { useReadChatRoomMessagesMutation } from 'store/api/yay/chat'

import Loading from 'components/Loading'
import LoadMore from 'components/LoadMore'
import { ChatMessage } from './ChatMessage'
import { snowplowEvent } from 'lib/analytics'

const SCROLL_TRESHOLD = 300

const ChatWindow = ({
  chatRoom,
  onPreviewMedia,
  onDeleteMessage,
  onReport,
  onReply,
  onJumpToMessage,
  onResetAroundMessage,
  focusId,
  messages,
  isFetching,
  toMessageId,
  toMessageIdSet,
  isFetchingTop,
  hasMoreTop,
  fromMessageIdSet,
  isFetchingBottom,
  hasMoreBottom,
}: {
  chatRoom: ChatRoom
  onPreviewMedia: (payload: { type: 'image' | 'video'; url: string }) => void
  onDeleteMessage: (messageId: number) => void
  onReport: (userId: number) => void
  onReply: (message: Message) => void
  onJumpToMessage: (id: number) => void
  onResetAroundMessage: () => void
  focusId: number
  messages: Array<Message>
  isFetching: boolean

  toMessageId: number
  toMessageIdSet: Function
  isFetchingTop: boolean
  hasMoreTop: boolean

  fromMessageIdSet?: Function
  isFetchingBottom?: boolean
  hasMoreBottom?: boolean
}) => {
  const chatWindowRef = useRef<HTMLDivElement>(null)
  const chatWindowPrevScrollTopRef = useRef<number>(null)
  const { diff, scrolling } = useScroll({ target: chatWindowRef })

  const [mounted, setMounted] = useState(false)
  const [focusTempId, setFocusTempId] = useState(0)
  const [isTooFar, setIsTooFar] = useState(false)
  const [smoothScroll, setSmoothScroll] = useState(0)

  const [readChatRoomMessages] = useReadChatRoomMessagesMutation()

  const { data: currentUser } = useCurrentUser()

  const latestMessage = last(messages)

  const loadMoreTop = () => {
    if (!messages?.[0]?._sending) toMessageIdSet(messages?.[0]?.id)
  }

  const { loadMoreRef: loadMoreTopRef } = useInfinityScrollSimple({
    loadMore: loadMoreTop,
  })

  const loadMoreBottom = () => {
    fromMessageIdSet?.(latestMessage?.id)
  }

  const { loadMoreRef: loadMoreBottomRef } = useInfinityScrollSimple({
    loadMore: loadMoreBottom,
  })

  const onScrollToBottom = () => {
    if (hasMoreBottom) {
      onResetAroundMessage()
    } else {
      setSmoothScroll(chatWindowRef.current.scrollHeight)
    }
  }

  useEffect(() => {
    if (!chatWindowRef.current) return

    const { clientHeight, scrollHeight, scrollTop } = chatWindowRef.current

    if (clientHeight < 66) {
      setIsTooFar(false)
      return
    }

    setIsTooFar(scrollHeight - scrollTop > 1.5 * clientHeight)
  }, [scrolling])

  // Maintain a scrolling position while scrolling up, loading older messages.
  useEffect(() => {
    if (toMessageId != 0 && !focusTempId) {
      chatWindowRef.current.scrollTop =
        chatWindowRef.current.scrollHeight - chatWindowPrevScrollTopRef.current
    }

    chatWindowPrevScrollTopRef.current = chatWindowRef.current.scrollHeight
  }, [messages?.[0]?.id])

  // Scroll to bottom when there is a new message and not too far from bottom.
  useEffect(() => {
    if (!chatWindowRef.current) return

    // scroll if send new message
    if (latestMessage?._sending) {
      onScrollToBottom()
      return
    }

    if (focusTempId || hasMoreBottom) return

    const { clientHeight, scrollHeight, scrollTop } = chatWindowRef.current

    if (!mounted && latestMessage?.id) {
      chatWindowRef.current.scrollTop = scrollHeight
      setMounted(true)
      return
    }

    if (
      scrollHeight - scrollTop - clientHeight <=
      Math.min(clientHeight / 2, SCROLL_TRESHOLD)
    ) {
      setSmoothScroll(scrollHeight)
    }
  }, [latestMessage?.id])

  // mark message as read
  useEffect(() => {
    if (
      !hasMoreBottom &&
      latestMessage?.id &&
      latestMessage?.user_id != currentUser?.id
    ) {
      readChatRoomMessages({
        chatId: chatRoom?.id,
        messageId: latestMessage?.id,
      })
    }
  }, [latestMessage?.id])

  const groupedMessages = useMemo(() => {
    const currentYear = dayjs().format('YYYY')

    return groupBy(messages, (message) => {
      const date = dayjs(message?.created_at * 1000)
      const isSameYear = currentYear === date.format('YYYY')

      return dayjs(message?.created_at * 1000).format(
        isSameYear ? 'M/D - dddd' : 'YYYY/M/D - dddd'
      )
    })
  }, [messages])

  useEffect(() => {
    snowplowEvent.scroll({ diff })
  }, [diff])

  useEffect(() => {
    if (focusId) {
      setFocusTempId(focusId)

      const target = document.getElementById(`Messages__item--${focusId}`)

      if (target) {
        const offset = Math.min(chatWindowRef.current.clientHeight / 3, 300)

        setSmoothScroll(target.offsetTop - offset)
      }
    } else {
      if (!scrolling) {
        setFocusTempId(0)
      }
    }
  }, [focusId, scrolling])

  useEffect(() => {
    if (smoothScroll !== 0) {
      chatWindowRef.current.scrollTop = Math.max(0, smoothScroll)

      const timeout = setTimeout(() => {
        setSmoothScroll(0)
      }, 200)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [smoothScroll])

  return (
    <div
      className={cx('ChatWindow', {
        'ChatWindow--smooth': smoothScroll && mounted,
      })}
      ref={chatWindowRef}
    >
      {messages?.length == 0 && isFetching ? (
        <Loading />
      ) : (
        <div className="Messages">
          <div className="Messages__wrapper">
            <LoadMore
              ref={loadMoreTopRef}
              className="Messages__load-more"
              isLoading={isFetchingTop}
              onClick={loadMoreTop}
              hidden={!hasMoreTop}
            />

            {entries(groupedMessages).map(([date, messages], i) => (
              <Fragment key={`${date}-${messages?.[0]?.id ?? i}`}>
                <p className="Messages__day">
                  <span>{date}</span>
                </p>

                {messages.map((message, i) => {
                  // not render temp message if still have more to load at bottom
                  if (message._sending && hasMoreBottom) return null

                  const messageOwner = (chatRoom?.members ?? [])?.find?.(
                    (member) => member.id == message?.user_id
                  )

                  const parentMessageOwner = (chatRoom?.members ?? [])?.find?.(
                    (member) => member.id == message?.parent?.user_id
                  )

                  return (
                    <ChatMessage
                      key={`${message}-${i}`}
                      message={message}
                      messageOwner={messageOwner}
                      parentMessageOwner={parentMessageOwner}
                      previousMessageOwnerId={messages[i - 1]?.user_id}
                      isGroupChat={chatRoom.is_group}
                      onPreviewMedia={onPreviewMedia}
                      onDeleteMessage={onDeleteMessage}
                      onReport={onReport}
                      onReply={onReply}
                      onJumpToMessage={onJumpToMessage}
                      focus={focusTempId === message.id}
                    />
                  )
                })}
              </Fragment>
            ))}

            <LoadMore
              ref={loadMoreBottomRef}
              className="Messages__load-more"
              isLoading={isFetchingBottom}
              onClick={loadMoreBottom}
              hidden={!hasMoreBottom}
            />
          </div>

          {mounted && (hasMoreBottom || isTooFar) && (
            <div className="Messages__scroll-down" onClick={onScrollToBottom}>
              <img src="/images/icon-arrow-down.svg" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ChatWindow
